// Shared attendance kiosks — one sign-in that many employees use to mark
// attendance from a single device (the tablet at the gate, the reception desk).
//
// The generated password is returned by exactly two calls (create and
// reset-password) and by nothing else, ever: hrms stores an argon2 hash, so an
// admin who closes the dialog without copying it must reset instead. Both call
// sites below are typed to make that obvious.
import api from './api';

export interface Kiosk {
  id: string;
  name: string;
  /** The generated sign-in address. Globally unique — the kiosk login screen
   *  has no organisation picker in front of it, so the address alone has to
   *  identify one kiosk. */
  login_email: string;
  is_active: boolean;
  /** Phase 2: require a face check before a punch is accepted. Stored and
   *  editable now, not yet enforced by the backend. */
  require_face: boolean;
  member_count: number;
  created_at?: string | null;
  last_login_at?: string | null;
}

/** A create/reset response — the ONLY place the plaintext password exists. */
export interface KioskWithPassword extends Kiosk {
  password: string;
}

export interface KioskMember {
  emptID: string;
  user_id?: string | null;
  name: string;
  employeeID?: string | null;
  designation?: string | null;
  profile_photo_url?: string | null;
}

export interface KioskOption {
  id: string;
  name: string;
  login_email: string;
}

export const listKiosks = async (): Promise<Kiosk[]> =>
  (await api.get<{ kiosks: Kiosk[] }>('/attendance/kiosk')).data.kiosks ?? [];

/** Active kiosks only, for the Employee form dropdown. View-level on the
 *  backend, so whoever can edit an employee can populate it. */
export const listKioskOptions = async (): Promise<KioskOption[]> =>
  (await api.get<{ kiosks: KioskOption[] }>('/attendance/kiosk/options')).data.kiosks ?? [];

export const createKiosk = async (body: {
  name: string;
  /** What the admin types becomes the sign-in address: a full email is used
   *  verbatim, anything else is slugified and given the kiosk domain. */
  username?: string;
  password?: string;
  require_face?: boolean;
}): Promise<KioskWithPassword> =>
  (await api.post<KioskWithPassword>('/attendance/kiosk', body)).data;

export const updateKiosk = async (
  id: string,
  body: {
    name?: string;
    is_active?: boolean;
    require_face?: boolean;
    /** Changes the sign-in address. A full address ("gate@company.com") is kept
     *  as typed; a bare username ("gate-1") gets the kiosk domain appended.
     *  Tablets already signed in keep working — only the next sign-in needs it. */
    username?: string;
  },
): Promise<Kiosk> => (await api.patch<Kiosk>(`/attendance/kiosk/${id}`, body)).data;

/** Mints a new password. Tablets already signed in keep working — switch the
 *  kiosk off if the point is to cut them off. */
export const resetKioskPassword = async (
  id: string,
): Promise<{ id: string; login_email: string; password: string }> =>
  (await api.post(`/attendance/kiosk/${id}/reset-password`)).data;

export const deleteKiosk = async (id: string): Promise<void> => {
  await api.delete(`/attendance/kiosk/${id}`);
};

export const getKioskMembers = async (id: string): Promise<KioskMember[]> =>
  (await api.get<{ members: KioskMember[] }>(`/attendance/kiosk/${id}/members`)).data.members ?? [];

/** REPLACE semantics: the list posted becomes the roster. An employee who is
 *  on another kiosk is MOVED here — one employee belongs to one kiosk. */
export const replaceKioskMembers = async (
  id: string,
  employeeIds: string[],
): Promise<KioskMember[]> =>
  (await api.put<{ members: KioskMember[] }>(`/attendance/kiosk/${id}/members`, {
    employee_ids: employeeIds,
  })).data.members ?? [];

/** The Employee form dropdown. `kioskId: null` removes the employee from
 *  whichever kiosk they are on. */
export const assignEmployeeToKiosk = async (
  emptID: string,
  kioskId: string | null,
): Promise<{ emptID: string; kiosk_id: string | null }> =>
  (await api.post('/attendance/kiosk/assign-employee', { emptID, kiosk_id: kioskId })).data;

export const getEmployeeKiosk = async (
  emptID: string,
): Promise<string | null> =>
  (await api.get<{ kiosk_id: string | null }>(`/attendance/kiosk/for-employee/${emptID}`)).data
    .kiosk_id ?? null;
